<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    >
      <drop-down
        v-if="anyChecked"
        always-show-dropdown
        class="actions-dropdown pt-2 text-black"
        :items="checkedActions"
        normal-size
      />
    </page-header>

    <div v-if="isDenying" class="row bg-secondary-3 p-3 rounded mr-0 ml-0 mb-4">
      <div class="col-12 my-3">
        <i class="fas fa-times-circle text-red" />
        <strong class="ml-2">
          Denegar
        </strong>
      </div>
      <div class="col-12">
        <validation-provider
          v-slot="{ errors, validate }"
          :custom-messages="{
            required: 'El campo es requerido.',
          }"
          rules="required"
        >
          <custom-text-area
            v-model="comment"
            :count-classes="['d-block', 'small-counter']"
            :custom-error-msg="errors[0]"
            :error="errors[0]"
            label="Comentario:"
            :maxlength="3000"
            show-char-count
          />
          <div class="text-right mt-4">
            <action-button
              class="mr-2"
              i="fas fa-times-circle"
              variant="outline-danger"
              @click="onCancel"
            >
              Cancelar
            </action-button>

            <action-button
              i="fas fa-save"
              variant="success"
              @click="() => applyAction(ACTIONS.Denie, validate)"
            >
              Guardar
            </action-button>
          </div>
        </validation-provider>
      </div>
    </div>

    <filter-manager
      ref="filterRef"
      v-model="filter.model"
      class="mb-4"
      :filters="filtersConfig"
      @search="onSearchHandler()"
    />

    <div v-if="showChecks" class="row my-3">
      <div class="col-12">
        <checkbox
          v-model="allChecked"
          checked-bg-color="bg-success"
          class="d-flex justify-content-end text-green"
          icon="fal fa-check"
          :text="allCheckedText"
          @input="checkAll"
        />
      </div>
    </div>

    <custom-separator class="my-4" text="Resultado" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No cuenta con información disponible."
    />

    <result-row
      v-for="item in results"
      :key="item.id"
      :checkable="showChecks"
      :model="item"
      @check="onCheckItem"
      @refresh="onSearchHandler"
    />

    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
//components
import FilterManager from 'FilterManager';
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import DropDown from 'dropdown';

import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import ResultRow from './components/ResultRow.vue';
import Checkbox from 'CheckBox';
import CustomTextArea from 'CustomTextArea';

//services
import {
  get,
  applyAction,
} from '@/services/medicaid/medicaid-certification.api';
import { getEligibilityStatus } from '@/services/common/code.api.js';
import { getCompaniesWithNationalIdentifier } from '@/services/provider/provider.api';
import medicaidEligibilityStatus from '@/utils/constants/medicaidEligibilityStatus';

//constants
const DEFAULT_PAGE_SIZE = 5;

const ACTIONS = Object.freeze({
  Approve: 1,
  Denie: 2,
});

export default {
  name: 'MedicaidCertificationMain',
  components: {
    FilterManager,
    PageHeader,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    Checkbox,
    CustomTextArea,
    DropDown,
    ActionButton,
  },
  mixins: [alertMixin],
  data() {
    return {
      headerConfig: {
        title: 'Certificación Medicaid',
        icon: 'fas fa-file-certificate',
        routes: ['Medicaid', ' Certificación Medicaid'],
      },
      filter: {
        model: {
          status: null,
          provider: null,
          number: '',
        },
        options: {
          statuses: [],
          providers: [],
        },
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      results: [],
      allCheckedIgnoreCallback: false,
      allChecked: false,
      comment: null,
      isDenying: false,
    };
  },
  computed: {
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          visible: !this.isAdding,
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({
              name: 'medicaid',
            });
          },
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'provider',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.filter.options.providers,
          initialValue: this.filter.model.provider,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          disabled: this.filter.options.providers.length === 1,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.filter.options.statuses,
          initialValue: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
        },
        {
          name: 'number',
          component: 'CustomInput',
          class: 'col-md-4',
          placeholder: 'Número de NPI',
          clearable: true,
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
        },
        {
          name: 'startDate',
          component: 'customDatePicker',
          class: 'col-md-3 w-100',
          placeholder: 'DD/MM/YYYY',
          label: 'Fecha Inicio:',
          isAdvanced: true,
        },
        {
          name: 'endDate',
          component: 'customDatePicker',
          class: 'col-md-3 w-100',
          placeholder: 'DD/MM/YYYY',
          label: 'Fecha Fin:',
          isAdvanced: true,
        },
      ];
    },
    showChecks() {
      return (
        this.allSubmitted &&
        this.filterSubmitted &&
        this.userHasPermissions('rempro.medicaid.medicaideligibility.determine')
      );
    },
    filterSubmitted() {
      return (
        this.filter.model.status?.id === medicaidEligibilityStatus.Submitted
      );
    },
    allSubmitted() {
      return (
        this.results.every(
          (x) => x.status.id === medicaidEligibilityStatus.Submitted
        ) && Boolean(this.results.length)
      );
    },
    allCheckedText() {
      return this.allChecked
        ? `Deseleccionar Todo (${this.results.filter((x) => x.checked).length})`
        : `Seleccionar Todo (${this.results.filter((x) => x.checked).length})`;
    },
    anyChecked() {
      return this.results.some((x) => x.checked);
    },
    checkedActions() {
      return [
        {
          visible: this.allSubmitted,
          label: 'Aprobar',
          icon: 'fas fa-check-circle text-green',
          click: () => this.applyAction(ACTIONS.Approve),
        },
        {
          label: 'Denegar',
          icon: 'fas fa-times-circle text-red',
          visible: this.allSubmitted,
          click: () => this.applyAction(ACTIONS.Denie),
        },
      ];
    },
    ACTIONS() {
      return ACTIONS;
    },
  },
  async mounted() {
    await this.getEligibilityStatus();
    await this.getProviders();
  },
  methods: {
    async getProviders() {
      const { data } = await getCompaniesWithNationalIdentifier();
      this.filter.options.providers = data;

      if (data.length === 1) {
        this.filter.model.provider = data[0];
      }
    },
    async getEligibilityStatus() {
      try {
        const { data } = await getEligibilityStatus();
        this.filter.options.statuses = data;
      } catch (error) {
        this.showAlertError('Error al cargar los estatus de elegibilidad.');
      }
    },
    async onSearchHandler(page = 1) {
      this.pagination.currentPage = page;

      const payload = {
        companyId: this.filter.model.provider?.id,
        statusId: this.filter.model.status?.id,
        number: this.filter.model.number,
        startDate: this.filter.model.startDate,
        endDate: this.filter.model.endDate,
        currentPage: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      const { data: results = { data: [] } } = await get(payload);
      this.results = results.data.map((x) => ({
        ...x,
        opened: false,
        checked: false,
      }));

      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(page);
    },
    checkAll() {
      if (this.allCheckedIgnoreCallback) return;
      this.results.forEach((x) => (x.checked = this.allChecked));
    },
    onCheckItem({ checked }) {
      if (checked) {
        const every = this.results.every((x) => x.checked);
        if (every) this.allChecked = true;
      } else if (this.allChecked) {
        this.allCheckedIgnoreCallback = true;
        this.allChecked = false;
        setTimeout(() => (this.allCheckedIgnoreCallback = false), 100);
      }
    },
    async applyAction(action, validateFn = null) {
      try {
        const ids = this.results.filter((x) => x.checked).map((x) => x.id);
        const isPlural = ids.length > 1;

        let message = '¿Está seguro que desea determinar en ';
        let successMsg = '';

        switch (action) {
          case ACTIONS.Approve:
            message += isPlural
              ? `aprobadas la elegibilidad de las (${ids.length}) certificaciones seleccionadas?`
              : `aprobada la elegibilidad de la (${ids.length}) certificación seleccionada?`;
            successMsg = isPlural ? 'aprobadas' : 'aprobada';
            break;
          case ACTIONS.Denie:
            message += isPlural
              ? `denegadas la elegibilidad de las (${ids.length}) certificaciones seleccionadas?`
              : `denegada la elegibilidad de la (${ids.length}) certificación seleccionada?`;
            successMsg = isPlural ? 'denegadas' : 'denegada';
            if (!this.isDenying) {
              this.isDenying = true;
              this.comment = null;
              return;
            }

            if (validateFn) {
              const { valid } = await validateFn();
              if (!valid) return;
            }
            break;
        }
        const confirmed = await this.ShowCancelModal('Confirmación', message);
        if (!confirmed) return;

        await applyAction({ action, ids, comment: this.comment });

        this.ShowToast(
          'Éxito',
          isPlural
            ? `(${ids.length}) Certificaciones han sido ${successMsg} exitosamente.`
            : `(${ids.length}) Certificación ha sido ${successMsg} exitosamente.`,
          'success'
        );

        await this.onSearchHandler(this.pagination.currentPage);
        this.isDenying = false;
        this.comment = null;
        this.allChecked = false;
      } catch (error) {
        this.ShowToast('Error', error.response?.data?.message, 'error');
      }
    },
    onCancel() {
      this.isDenying = false;
      this.comment = null;
    },
  },
};
</script>
<style scoped>
::v-deep .orange-icon {
  font-size: 18pt;
}
::v-deep .custom-datetime-picker-container {
  width: 100% !important;
}
</style>
